import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { callGet } from '@/api/fetcher';
import { MAX_FILTER_VALUES } from '@/components/pages/ActivityLogs/constants';
import { getNextCursor } from '@/hooks/queryCache/nextCursor';
import { type DateSelection } from '@/modules/analytics/dateSelection';
import { getPermissions } from '@/redux/permissions/selectors';
import { AuditLogs } from '@/shared/types/auditLogs';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { addDaysToDate, formatDate } from '@/util/dateUtils';

type AuditLogsParams = {
  account_slug: string;
  next_cursor?: string;
  brain_id?: string;
  desk_id?: string;
  log_type?: 'authoring' | 'other';
  start_date?: string;
  end_date?: string;
  source_ids?: string[];
  brain_ids?: string[];
  desk_ids?: string[];
  target_ids?: string[];
  target_types?: string[];
};

const DEFAULT_LIMIT = 30;
const ONE_MINUTE = 60 * 1000;
const DEFAULT_LOOKBACK_DAYS = 6;

// One repeated key per multi-select filter. source_ids / target_types may hold non-UUID
// values (system sources, target-type names), so values are sent as-is — the backend validates.
const ARRAY_PARAM_KEYS = [
  'source_ids',
  'brain_ids',
  'desk_ids',
  'target_ids',
  'target_types',
] as const;

const getScalarParams = (params: AuditLogsParams) =>
  Object.entries(params).filter(
    ([key, value]) => value !== undefined && !(ARRAY_PARAM_KEYS as readonly string[]).includes(key)
  ) as [string, string][];

// Dedupe then cap: the only guard every caller shares. The page hook's `setFilter` cap misses
// URL-hydrated filters and the prop-driven Home embedding.
const appendArrayParams = (queryParams: URLSearchParams, params: AuditLogsParams) => {
  ARRAY_PARAM_KEYS.forEach((key) => {
    [...new Set(params[key])]
      .slice(0, MAX_FILTER_VALUES)
      .forEach((value) => queryParams.append(key, value));
  });
};

export const endpoints = Object.freeze({
  auditLogs: ({ next_cursor, ...params }: AuditLogsParams) => {
    const queryParams = new URLSearchParams(getScalarParams(params));
    appendArrayParams(queryParams, params);
    if (next_cursor) {
      queryParams.append('next_cursor', next_cursor);
    }
    queryParams.set('limit', String(DEFAULT_LIMIT));
    return `/www/api/v1/audit_logs?${queryParams}`;
  },
});

const API = {
  listAuditLogs: async (params: AuditLogsParams): Promise<AuditLogs> =>
    callGet(endpoints.auditLogs(params)),
};

export interface ActivityLogDateRange {
  startDate: string;
  endDate: string;
}

export const DEFAULT_ACTIVITY_LOG_SELECTION: DateSelection = {
  type: 'preset',
  days: DEFAULT_LOOKBACK_DAYS,
};

/** Default Activity Log range: last 7 days (today minus 6 → today), formatted as `yyyy-MM-dd`. */
export const getDefaultRange = (): ActivityLogDateRange => ({
  startDate: formatDate(addDaysToDate(new Date(), -DEFAULT_LOOKBACK_DAYS), 'yyyy-MM-dd'),
  endDate: formatDate(new Date(), 'yyyy-MM-dd'),
});

export interface UseActivityLogsOptions {
  logType?: 'authoring' | 'other';
  // Deduped and capped at the request boundary. Callers only collapse "all selected" to omitted.
  sourceIds?: string[];
  brainIds?: string[];
  deskIds?: string[];
  targetIds?: string[];
  targetTypes?: string[];
  // Single-value scoping used by the prop-driven Home embedding.
  brainId?: string | null;
  deskId?: string | null;
  startDate?: string;
  endDate?: string;
  /** Skip the request entirely — an empty scope must not fall back to the whole account. */
  enabled?: boolean;
}

export const useActivityLogs = (slug: string, options: UseActivityLogsOptions = {}) => {
  const {
    logType,
    sourceIds,
    brainIds,
    deskIds,
    targetIds,
    targetTypes,
    brainId,
    deskId,
    startDate: propStartDate,
    endDate: propEndDate,
    enabled = true,
  } = options;

  const canReadLogs = useSelector((state: RootState) =>
    getPermissions(state, 'audit_logs', actions.READ)
  );

  // Audit logs are filtered by exact `created` timestamp, so the selected day labels must
  // map to the user's LOCAL day boundaries (matching the analytics chat-logs path), not naive
  // UTC days — otherwise activity near local midnight is excluded. Default to the last 7 days.
  const { startDate, endDate } = useMemo(() => {
    if (propStartDate && propEndDate) {
      return { startDate: propStartDate, endDate: propEndDate };
    }
    return getDefaultRange();
  }, [propStartDate, propEndDate]);

  const { startDate: formattedStartDate, endDate: formattedEndDate } = getAnalyticsRangeLocal(
    startDate,
    endDate
  );

  // Built fresh each render: its only consumers are the value-compared query-key string and the
  // per-render queryFn closure, so memoizing the object buys nothing.
  const baseParams: AuditLogsParams = {
    account_slug: slug,
    log_type: logType,
    brain_id: brainId ?? undefined,
    desk_id: deskId ?? undefined,
    source_ids: sourceIds,
    brain_ids: brainIds,
    desk_ids: deskIds,
    target_ids: targetIds,
    target_types: targetTypes,
    start_date: formattedStartDate,
    end_date: formattedEndDate,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: [endpoints.auditLogs(baseParams)],
    queryFn: ({ pageParam }) =>
      API.listAuditLogs({ ...baseParams, next_cursor: pageParam?.cursor }),
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor?: string },
    staleTime: ONE_MINUTE, // until RTM is ready to send us updates
    retry: 5,
    enabled: enabled && Boolean(slug) && canReadLogs,
  });

  const isDataEmpty = data?.pages[0]?.audit_logs?.length === 0;

  return { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isDataEmpty };
};
